import type { TFunction } from 'i18next';
import type { ApprovalAttributes } from '@/types/approval';
import {
  SERP_NOT_LOADED_MESSAGE,
  type SerpDocumentRef,
} from '@/utils/approval/serpDocument';

/** SERP 재고실사 조정(다중) — 레거시 appr_edit_forms_serp_inventory_multi */
export const SERP_BULK_STOCK_ADJUSTMENT_TYPE = 210;

export const isSerpBulkStockAdjustmentType = (apvType: number): boolean =>
  apvType === SERP_BULK_STOCK_ADJUSTMENT_TYPE;

export const SERP_BULK_DETAIL_NOT_FOUND_MESSAGE =
  '상세 데이터를 찾을 수 없습니다.';

export type SerpBulkInspectionRow = {
  SSN_CD?: unknown;
  STD_STK_QTY?: unknown;
  SALE_AMT?: unknown;
  STCKT_QTY?: unknown;
  STCKT_AMT?: unknown;
  GAP_QTY?: unknown;
  GAP_AMT?: unknown;
  LOSS_AMT?: unknown;
};

export type SerpBulkSettlementRow = {
  SSN_CD?: unknown;
  LOSS_AMT?: unknown;
  GI_RAT?: unknown;
  DED_RTO?: unknown;
  STMT_AMT?: unknown;
  APRV_RMK?: unknown;
};

export type SerpBulkShop = {
  SHOP_CD?: unknown;
  SHOP_NM?: unknown;
  STCKT_DE?: unknown;
  SM_SHOP_CD?: unknown;
  SM_SHOP_NM?: unknown;
  SM_SHOP_STS_DE?: unknown;
  SM_SHOP_BRN?: unknown;
  SM_SHOP_RMK?: unknown;
  EXC_AMT_NUM?: unknown;
  SM_SURETY_BOND_AMT_NUM?: unknown;
  STK_INVES_LIST?: unknown;
  STMT_LIST?: unknown;
  [key: string]: unknown;
};

/** EAI 응답 원문 — 저장 때 문자열로 감싸 attributes.data.data 에 보관(레거시 동일) */
export type SerpBulkStockAdjustmentSource = {
  APRV_TITLE?: unknown;
  BRAND_NM?: unknown;
  STK_INVES_TYP_CD?: unknown;
  STK_INVES_TYP_NM?: unknown;
  INSTR_DEG?: unknown;
  SHOP_NM?: unknown;
  SHOP_LIST?: unknown;
  [key: string]: unknown;
};

export type SerpBulkStockAdjustmentDraftSource = {
  code: number;
  attributes: { typeCode?: string };
  display: { source?: SerpBulkStockAdjustmentSource | null };
};

/** sequence = 매장별 SERP 문서 번호를 쉼표로 이은 값 */
export type SerpBulkStockAdjustmentState = SerpDocumentRef & {
  source: SerpBulkStockAdjustmentSource;
  /** SERP 응답으로 자동 입력한 제목 — 이탈 경고는 이 값과 비교. 편집은 미사용 */
  autoSubject: string;
};

const asRows = <T>(raw: unknown): T[] => (Array.isArray(raw) ? raw : []);

export const getSerpBulkShops = (source: SerpBulkStockAdjustmentSource) =>
  asRows<SerpBulkShop>(source.SHOP_LIST);

export const getSerpBulkInspectionRows = (shop: SerpBulkShop) =>
  asRows<SerpBulkInspectionRow>(shop.STK_INVES_LIST);

export const getSerpBulkSettlementRows = (shop: SerpBulkShop) =>
  asRows<SerpBulkSettlementRow>(shop.STMT_LIST);

export const SERP_BULK_SUMMARY_SEASON = '합계';

export const findSerpBulkSummaryRow = (shop: SerpBulkShop) =>
  getSerpBulkInspectionRows(shop).find(
    (row) => row.SSN_CD === SERP_BULK_SUMMARY_SEASON,
  );

/** 콤마 찍힌 글자·숫자 모두 콤마 숫자로. 빈 값은 빈칸 */
export const formatSerpBulkAmount = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '';
  const num = Number(String(value).replace(/,/g, ''));
  return Number.isFinite(num) ? num.toLocaleString('ko-KR') : String(value);
};

const toNumber = (value: unknown): number => {
  const num = Number(String(value ?? '').replace(/,/g, ''));
  return Number.isFinite(num) ? num : 0;
};

/** 정산 내역 합계 줄 — 응답 합계가 아니라 시즌 줄을 더함(레거시 동일) */
export const sumSerpBulkSettlement = (rows: SerpBulkSettlementRow[]) =>
  rows.reduce(
    (acc, row) => ({
      loss: acc.loss + (row.LOSS_AMT ? toNumber(row.LOSS_AMT) : 0),
      settlement: acc.settlement + (row.STMT_AMT ? toNumber(row.STMT_AMT) : 0),
    }),
    { loss: 0, settlement: 0 },
  );

export const getSerpBulkNumber = (value: unknown): number =>
  value ? toNumber(value) : 0;

/** 재고조사 유형 7 = 폐점 정산 */
const isStoreClosureSettlement = (source: SerpBulkStockAdjustmentSource) =>
  String(source.STK_INVES_TYP_CD ?? '').trim() === '7';

/**
 * 유형 7 은 '{최상위 SHOP_NM} 매장 폐점 정산의 건', 그 외는 SERP 결재제목. 값이 비면 빈 제목(레거시 동일).
 * 210 응답은 매장명이 SHOP_LIST 안에만 있어 유형 7 은 빈 제목이 됨 — 레거시와 같게 둠
 */
export const buildSerpBulkStockAdjustmentSubject = (
  source: SerpBulkStockAdjustmentSource,
  t: TFunction,
): string => {
  if (!isStoreClosureSettlement(source)) {
    return source.APRV_TITLE ? String(source.APRV_TITLE) : '';
  }
  return source.SHOP_NM
    ? t('{{shop}} 매장 폐점 정산의 건', {
        shop: String(source.SHOP_NM),
        interpolation: { escapeValue: false },
      })
    : '';
};

const readString = (raw: unknown): string | null =>
  typeof raw === 'string' && raw.trim() ? raw.trim() : null;

/** 저장 attributes → 다시 조회할 SERP 문서. 문서 번호는 최상위가 아니라 data.sequence */
export const parseSerpBulkDocumentRef = (
  attributes: unknown,
): SerpDocumentRef | null => {
  if (!attributes || typeof attributes !== 'object') return null;
  const record = attributes as Record<string, unknown>;
  const typeCode = readString(record.typeCode);
  const data = record.data;
  const sequence =
    data && typeof data === 'object'
      ? readString((data as Record<string, unknown>).sequence)
      : null;
  return typeCode && sequence ? { typeCode, sequence } : null;
};

export const checkSerpBulkStockAdjustmentBeforeSave = (
  state: SerpBulkStockAdjustmentState | null | undefined,
): string | null => (state?.source ? null : SERP_NOT_LOADED_MESSAGE);

/** 최상위 sequence 는 비우고 문서 번호는 data.sequence — 서버 상태 전송이 이 값을 나눠 매장별로 보냄 */
export const buildSerpBulkStockAdjustmentAttributes = (
  state: SerpBulkStockAdjustmentState | null | undefined,
  content: string,
): ApprovalAttributes | null =>
  state?.source
    ? {
        typeCode: state.typeCode,
        content,
        data: {
          sequence: state.sequence,
          data: JSON.stringify(state.source),
        },
      }
    : null;
